import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchApi } from "../../../hooks";

type ConversationBannerType = {
  id: string;
  name: string;
  dueDate: string;
  completedCount: number;
  totalCount: number;
};

type StoreItemType = { exp: number }

const STORE_DURATION = 60 * 60 * 24 * 7; // 7 days

const generateKey = () => {
  if (!window.uData) {
    return undefined;
  }

  return [window.uData.org_id, window.uData.user_id, "conversationBanner"].join("_")
}

const conversationBannerStore = {
  key: undefined,
  getStored: () => {
    if (!conversationBannerStore.key) {
      const key = generateKey();
      if (!key) {
        return {};
      }
      conversationBannerStore.key = key
    }

    const stored = localStorage.getItem(conversationBannerStore.key);
    const parsed: Record<string, StoreItemType> = stored ? JSON.parse(stored) : {};

    // remove expired data
    Object.entries(parsed).forEach(([key, value]) => {
      if (value.exp < ((Date.now() / 1000) - STORE_DURATION)) {
        delete parsed[key];
      }
    });
    localStorage.setItem(conversationBannerStore.key, JSON.stringify(parsed));
    return parsed;
  },
  getItem: (item: ConversationBannerType) => {
    const parsed = conversationBannerStore.getStored();
    return !!parsed[item.id];
  },
  setItem: (item: ConversationBannerType, value: boolean) => {
    const parsed = conversationBannerStore.getStored();
    if (!value) {
      delete parsed[item.id];
    }
    else {
      parsed[item.id] = { exp: Math.floor(Date.now() / 1000) };
    }

    localStorage.setItem(conversationBannerStore.key, JSON.stringify(parsed));
  },
}

const query = `#graphql
  query conversationEvents(
    $page: Int,
    $limit: Int,
    $query: String,
    $stFilter: ConversationEventSearchFilterInput,
    $orderBy: [String!],
    $scope: String,
    $orgId: String
  ) {
    conversationEvents(
      page: $page,
      limit: $limit,
      query: $query,
      stFilter: $stFilter,
      orderBy: $orderBy,
      scope: $scope,
      orgId: $orgId
    ) {
      total
      page
      limit
      items {
        conversation {
          id
          name
          endDate
        }
      }
    }
  }
`

type ResponseData = {
  data: {
    conversationEvents: {
      total: number;
      items: {
        conversation: {
          name: string;
          id: string;
          endDate: string;
        }
      }[];
    }
  }
};

const useDataFetcher = () => {
  const [{data, ...rest}] = useSearchApi<ResponseData>({
    query,
    variables: {
      page: 0,
      limit: 1,
      stFilter: {
        operator: "and",
        fields: {
          status: { eq: "1" },
        },
        filters: [{
          operator: "or",
          fields:  {
              memberId: { eq: window.uData.user_id },
              managerId: { eq: window.uData.user_id }
          }
        }]
      },
      orderBy: ['Conversation.endDate ASC']
    }
  });

  const mappedData: ConversationBannerType = data?.data?.conversationEvents?.items?.[0]?.conversation ? {
    id: data.data.conversationEvents.items[0].conversation.id,
    name: data.data.conversationEvents.items[0].conversation.name,
    dueDate: data.data.conversationEvents.items[0].conversation.endDate,
    completedCount: 1,
    totalCount: data.data.conversationEvents.total
  } : null;

  return [{ data: mappedData, ...rest }];
  // mock data
  // return [
  //   {
  //     data: {
  //       id: "2",
  //       name: "Test",
  //       dueDate: "2022-01-01",
  //       completedCount: 1,
  //       totalCount: 2
  //     },
  //     loading: false,
  //     error: null
  //   }
  // ];
}


export const useConversationBanner = () => {
  const resultRef = useRef<ConversationBannerType>();
  const [display, setDisplay] = useState(false);
  const handleClose = useCallback(() => {
    setDisplay(false);
    if (!resultRef.current) {
      return;
    }
    conversationBannerStore.setItem(resultRef.current, true);
  }, []);

  const [{ data, loading, error }] = useDataFetcher();

  useEffect(() => {
    if (loading || error || !data) {
      return;
    }

    if (!resultRef.current) {
      resultRef.current = data;
    }

    const isItemStored = conversationBannerStore.getItem(resultRef.current);
    const hasConversation = data.totalCount >= 1;

    // display the banner if it is not stored
    setDisplay(!isItemStored && hasConversation);
  }, [data, loading, error]);

  const result = {
    item: resultRef.current ? Object.freeze(resultRef.current) : null,
    shouldDisplay: display,
    isLoading: loading,
    handleClose
  }
  return result;
};
